import { useQuery } from '@tanstack/react-query';
import { TenantsApi } from '../../namespace/api';
import apiClient from './apiClient';

export const WEBHOOK_RETRY_STRATEGY_NONE = 'none';
export const WEBHOOK_RETRY_STRATEGY_ONE = 'one';
export const WEBHOOK_RETRY_STRATEGY_THREE = 'three';
export const WEBHOOK_RETRY_STRATEGY_EXPONENTIAL = 'exponential';

export const WEBHOOK_FAILURE_MODE_NONE = 'none';
export const WEBHOOK_FAILURE_MODE_SUSPEND = 'suspend';
export const WEBHOOK_FAILURE_MODE_DIVERT = 'divert';
export const WEBHOOK_FAILURE_MODE_DIVERT_AND_SUSPEND = 'divertAndSuspend';

export const WEBHOOK_STATUS_CODES = [
	'4xx',
	'5xx',
	'400',
	'401',
	'402',
	'403',
	'404',
	'405',
	'406',
	'407',
	'408',
	'409',
	'410',
	'411',
	'412',
	'413',
	'414',
	'415',
	'416',
	'417',
	'418',
	'420',
	'422',
	'423',
	'424',
	'425',
	'426',
	'428',
	'429',
	'431',
	'444',
	'449',
	'450',
	'451',
	'499',
	'500',
	'501',
	'502',
	'503',
	'504',
	'505',
	'506',
	'507',
	'508',
	'510',
	'511',
];

export interface WebhookFailureHandlingResponse {
	mode: 'none' | 'suspend' | 'divert' | 'divertAndSuspend';
	actOnTimeout: boolean;
	actOnStatusCodes: string[];
}

export interface WebhookResponse {
	// Required
	name: string;
	displayName: string;
	integrationName: string;
	enabled: boolean;
	suspended: boolean;
	suspendedTimestamp: string;
	events: [string];
	retryStrategy: 'none' | 'one' | 'three' | 'exponential';
	failureHandling: WebhookFailureHandlingResponse;
	alertIntegrationName?: string;
	resetAlertIntegrationName?: boolean;
}

export interface WebhookRequest {
	// required
	name: string;
	integrationName: string;
	// optional
	displayName?: string;
	events?: string[];
	enabled?: boolean; // defaults to true
	retryStrategy?: 'none' | 'one' | 'three' | 'exponential'; // defaults to three
	failureHandling?: WebhookFailureHandlingResponse;
	alertIntegrationName?: string;
	resetAlertIntegrationName?: boolean;
}

export interface GetWebhookResponse {
	webhooks: WebhookResponse[];
}

export const getWebhooks = async (authToken: string) => {
	const res = await apiClient.get<GetWebhookResponse>('/webhooks', {
		headers: {
			Authorization: authToken,
		},
	});

	return res.data;
};

export const configureWebhook = async (
	request: WebhookRequest,
	authToken: string,
) => {
	const res = await apiClient.put<WebhookResponse>('/webhooks', request, {
		headers: {
			Authorization: authToken,
		},
	});

	return res.data;
};

export const deleteWebhook = async (name: string, authToken: string) => {
	await apiClient.delete(`/webhooks/${name}`, {
		headers: {
			Authorization: authToken,
		},
	});
};

export const useWebhookConfigurations = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	return useQuery(queryKey, async () => {
		const response = tenantHostname
			? await TenantsApi.getWebhooks(tenantHostname)
			: await getWebhooks(token);
		const webhookConfigurations = response?.webhooks;
		return webhookConfigurations;
	});
};

export const unsuspendWebhook = async (name: string, authToken: string) => {
	const res = await apiClient.patch<WebhookResponse>(
		`/webhooks/${name}/unsuspend`,
		{},
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

export const QUERY_KEYS = {
	WEBHOOK_CONFIGURATIONS: 'webhook_configurations',
};
